// src/components/Community.js
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Container, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Menu, MenuItem, Fab } from '@mui/material';
import { ThumbUp, ThumbUpOutlined, ChatBubbleOutline, Add, MoreVert, Close } from '@mui/icons-material';
import { AuthContext } from '../utils/AuthContext';
import { useCommunityNotifications } from '../utils/CommunityNotificationContext';
import { getPosts, getMorePosts, likePost, addComment, addPost, deletePost, deleteComment, editPost as editPostFn, updateUserLastCommunityVisit } from '../utils/firestoreUtils';
import Compressor from 'compressorjs';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../utils/firebase';

// Renders post text with clickable URLs and internal /calendar?event= links
function PostContent({ text }) {
  if (!text) return null;
  // Split on URLs (http/https) or internal links (/calendar?...)
  const urlRegex = /(https?:\/\/[^\s]+|\/calendar\?event=[^\s]+)/g;
  const parts = [];
  let last = 0, match;
  const str = String(text);
  while ((match = urlRegex.exec(str)) !== null) {
    if (match.index > last) parts.push({ type: 'text', value: str.slice(last, match.index) });
    const url = match[0];
    const isInternal = url.startsWith('/');
    parts.push({ type: 'link', value: url, internal: isInternal });
    last = match.index + url.length;
  }
  if (last < str.length) parts.push({ type: 'text', value: str.slice(last) });

  return (
    <span style={{ whiteSpace: 'pre-line' }}>
      {parts.map((part, i) =>
        part.type === 'text' ? (
          <span key={i}>{part.value}</span>
        ) : part.internal ? (
          <a key={i} href={part.value}
            style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
            View Event →
          </a>
        ) : (
          <a key={i} href={part.value} target="_blank" rel="noopener noreferrer"
            style={{ color: 'var(--accent)', textDecoration: 'none', wordBreak: 'break-all' }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
            {part.value}
          </a>
        )
      )}
    </span>
  );
}

function Community() {
  const { user } = useContext(AuthContext);
  const { clearNotification } = useCommunityNotifications();
  const [posts, setPosts] = useState([]);
  const loadingRef = useRef(false);
  const [expandedPost, setExpandedPost] = useState(null);
  const [commentTexts, setCommentTexts] = useState({});
  const [openNewTopic, setOpenNewTopic] = useState(false);
  const [topicContent, setTopicContent] = useState('');
  const [topicImage, setTopicImage] = useState(null);
  const [topicImagePreview, setTopicImagePreview] = useState(null);
  const [postMenuAnchor, setPostMenuAnchor] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [confirmDeletePost, setConfirmDeletePost] = useState(false);
  const [confirmDeleteComment, setConfirmDeleteComment] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [activeEditPost, setActiveEditPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editImage, setEditImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      const fetchedPosts = await getPosts();
      setPosts(fetchedPosts);
      setHasMorePosts(fetchedPosts.length === 20);
      loadingRef.current = false;
    };
    fetchPosts();
    if (user) {
      updateUserLastCommunityVisit(user.uid);
      clearNotification();
    }
  }, [user, clearNotification]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightboxSrc(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Scroll-based infinite load — attached to window/document scroll, not a nested container
  useEffect(() => {
    const handleScroll = () => {
      if (loadingRef.current || !hasMorePosts) return;
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (scrolled >= total - 200) loadMorePosts();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMorePosts, posts]);

  const refreshPosts = async () => {
    const fetchedPosts = await getPosts();
    setPosts(fetchedPosts);
  };

  // Like — optimistic local update, then fire-and-forget server call
  const handleLike = (postId) => {
    if (!user) return;
    setPosts(prev => prev.map(p => {
      if (p.id !== postId) return p;
      const liked = (p.likes || []).includes(user.uid);
      return {
        ...p,
        likes: liked
          ? p.likes.filter(id => id !== user.uid)
          : [...(p.likes || []), user.uid]
      };
    }));
    likePost(postId, user.uid); // fire and forget — no await, no re-fetch
  };

  const handleAddComment = async (postId) => {
    const text = commentTexts[postId];
    if (!user || !text?.trim()) return;
    setCommentTexts(prev => ({ ...prev, [postId]: '' }));
    await addComment(postId, user.uid, user.displayName || 'User', text);
    await refreshPosts();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    new Compressor(file, {
      quality: 0.8, maxWidth: 1600, maxHeight: 1600,
      success: (compressed) => {
        setTopicImage(compressed);
        const reader = new FileReader();
        reader.onload = (ev) => setTopicImagePreview(ev.target.result);
        reader.readAsDataURL(compressed);
      },
    });
  };

  const handleCreateTopic = async () => {
    if (!user || !topicContent.trim()) return;
    let imageUrl = null;
    if (topicImage) {
      const imageRef = ref(storage, `community/${Date.now()}_${topicImage.name}`);
      await uploadBytes(imageRef, topicImage);
      imageUrl = await getDownloadURL(imageRef);
    }
    await addPost({
      userId: user.uid, userName: user.displayName || 'User',
      type: 'post', content: topicContent, image: imageUrl,
      timestamp: new Date(), likes: [], comments: []
    });
    setOpenNewTopic(false);
    setTopicContent(''); setTopicImage(null); setTopicImagePreview(null);
    await refreshPosts();
  };

  const handleDeletePost = async () => {
    if (selectedPost) {
      await deletePost(selectedPost.id);
      setConfirmDeletePost(false); setPostMenuAnchor(null); setSelectedPost(null);
      await refreshPosts();
    }
  };

  const handleDeleteComment = async () => {
    if (commentToDelete) {
      await deleteComment(commentToDelete.postId, commentToDelete.commentIndex);
      setConfirmDeleteComment(false); setCommentToDelete(null);
      await refreshPosts();
    }
  };

  const handleEditImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    new Compressor(file, {
      quality: 0.8, maxWidth: 1600, maxHeight: 1600,
      success: (compressed) => {
        setEditImage(compressed);
        const reader = new FileReader();
        reader.onload = (ev) => setEditImagePreview(ev.target.result);
        reader.readAsDataURL(compressed);
      },
    });
  };

  const handleSaveEdit = async () => {
    if (!activeEditPost || !editContent.trim()) return;
    let imageUrl = activeEditPost.image;
    if (editImage) {
      const imageRef = ref(storage, `community/${Date.now()}_${editImage.name}`);
      await uploadBytes(imageRef, editImage);
      imageUrl = await getDownloadURL(imageRef);
    }
    await editPostFn(activeEditPost.id, { content: editContent, image: imageUrl });
    setActiveEditPost(null); setEditContent(''); setEditImage(null); setEditImagePreview(null);
    await refreshPosts();
  };

  const loadMorePosts = async () => {
    if (!hasMorePosts || loadingRef.current) return;
    loadingRef.current = true; setLoadingMore(true);
    try {
      const lastPost = posts[posts.length - 1];
      const morePosts = await getMorePosts(lastPost);
      if (morePosts.length === 0) setHasMorePosts(false);
      else setPosts(prev => [...prev, ...morePosts]);
    } finally {
      loadingRef.current = false; setLoadingMore(false);
    }
  };

  const formatDate = (timestamp) => {
    try {
      const d = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return ''; }
  };

  return (
    <Container maxWidth="xl" sx={{ pb: 10 }}>
      <div className="page-header">
        <h1 className="page-title">Community</h1>
        <p className="page-subtitle">Share your track experience</p>
      </div>

      <div className="community-feed">
        {posts.map((post) => (
          <div key={post.id} className="post-card">
            {/* Header */}
            <div className="post-header">
              <div className="post-avatar">
                {post.userHeadshot
                  ? <img src={post.userHeadshot} alt={post.userName} />
                  : (post.userName || 'U')[0].toUpperCase()}
              </div>
              <div className="post-meta">
                <Link to={`/users/${post.userId}`} className="post-author">{post.userName || 'Unknown'}</Link>
                <div className="post-timestamp">{formatDate(post.timestamp)}</div>
              </div>
              {user && user.uid === post.userId && (
                <button
                  style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: 'var(--text-tertiary)', borderRadius: '50%' }}
                  onClick={(e) => { setPostMenuAnchor(e.currentTarget); setSelectedPost(post); }}
                >
                  <MoreVert fontSize="small" />
                </button>
              )}
            </div>

            {/* Body */}
            <div className="post-body"><PostContent text={post.content} /></div>

            {/* Image */}
            {post.image && (
              <img
                className="post-image"
                src={post.image}
                alt="Post"
                onClick={() => setLightboxSrc(post.image)}
              />
            )}

            {/* Actions */}
            <div className="post-actions">
              <button
                className={`action-btn ${(post.likes || []).includes(user?.uid) ? 'liked' : ''}`}
                onClick={() => handleLike(post.id)}
              >
                {(post.likes || []).includes(user?.uid)
                  ? <ThumbUp fontSize="small" />
                  : <ThumbUpOutlined fontSize="small" />}
                {(post.likes || []).length || 0}
              </button>
              <button
                className="action-btn"
                onClick={() => setExpandedPost(expandedPost === post.id ? null : post.id)}
              >
                <ChatBubbleOutline fontSize="small" />
                {post.comments?.length || 0} comments
              </button>
            </div>

            {/* Comments */}
            {expandedPost === post.id && (
              <div className="comments-section">
                {post.comments?.map((comment, idx) => (
                  <div key={idx} className="comment-item">
                    <div className="post-avatar" style={{ width: 28, height: 28, fontSize: '0.7rem', flexShrink: 0 }}>
                      {(comment.userName || 'U')[0].toUpperCase()}
                    </div>
                    <div className="comment-bubble">
                      <Link to={`/users/${comment.userId}`} className="comment-name">
                        {comment.userName || 'User'}
                      </Link>
                      {comment.text}
                    </div>
                    {user && user.uid === comment.userId && (
                      <button
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: '0 4px', borderRadius: '50%' }}
                        onClick={() => { setCommentToDelete({ postId: post.id, commentIndex: idx }); setConfirmDeleteComment(true); }}
                      >
                        <Close fontSize="small" />
                      </button>
                    )}
                  </div>
                ))}
                {user && (
                  <div className="comment-input-row">
                    <input
                      className="comment-input"
                      placeholder="Add a comment…"
                      value={commentTexts[post.id] || ''}
                      onChange={(e) => setCommentTexts(prev => ({ ...prev, [post.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment(post.id); }}
                    />
                    <button className="comment-post-btn" onClick={() => handleAddComment(post.id)}>Post</button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {loadingMore && (
          <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
            Loading more…
          </div>
        )}
        {!hasMorePosts && posts.length > 0 && (
          <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-tertiary)', fontSize: '0.82rem' }}>
            You're all caught up ✓
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxSrc && (
        <div className="lightbox-overlay" onClick={() => setLightboxSrc(null)}>
          <img className="lightbox-img" src={lightboxSrc} alt="Full size" onClick={e => e.stopPropagation()} />
          <button className="lightbox-close" onClick={() => setLightboxSrc(null)}>✕</button>
        </div>
      )}

      {user && (
        <Fab color="primary" sx={{ position: 'fixed', bottom: 24, right: 24 }} onClick={() => setOpenNewTopic(true)}>
          <Add />
        </Fab>
      )}

      {/* New Post */}
      <Dialog open={openNewTopic} onClose={() => setOpenNewTopic(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Post</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="What's on your mind?" fullWidth multiline rows={4}
            value={topicContent} onChange={(e) => setTopicContent(e.target.value)} sx={{ mb: 2 }} />
          <input accept="image/*" style={{ display: 'none' }} id="topic-image-upload" type="file" onChange={handleImageUpload} />
          <label htmlFor="topic-image-upload">
            <Button variant="outlined" component="span" fullWidth sx={{ mb: 2, textTransform: 'none' }}>Upload Image</Button>
          </label>
          {topicImagePreview && (
            <img src={topicImagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewTopic(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleCreateTopic} variant="contained" disabled={!topicContent.trim()} sx={{ textTransform: 'none' }}>Post</Button>
        </DialogActions>
      </Dialog>

      {/* Post Menu */}
      <Menu anchorEl={postMenuAnchor} open={Boolean(postMenuAnchor)} onClose={() => { setPostMenuAnchor(null); setSelectedPost(null); }}>
        <MenuItem onClick={() => { setActiveEditPost(selectedPost); setEditContent(selectedPost?.content || ''); setEditImagePreview(selectedPost?.image); setPostMenuAnchor(null); }}>Edit</MenuItem>
        <MenuItem onClick={() => setConfirmDeletePost(true)} sx={{ color: 'error.main' }}>Delete</MenuItem>
      </Menu>

      <Dialog open={confirmDeletePost} onClose={() => setConfirmDeletePost(false)}>
        <DialogTitle>Delete Post</DialogTitle>
        <DialogContent>This will permanently delete the post and all its comments.</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeletePost(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleDeletePost} color="error" variant="contained" sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeleteComment} onClose={() => setConfirmDeleteComment(false)}>
        <DialogTitle>Delete Comment</DialogTitle>
        <DialogContent>Are you sure you want to delete this comment?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteComment(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleDeleteComment} color="error" variant="contained" sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(activeEditPost)} onClose={() => setActiveEditPost(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Post</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Post Content" fullWidth multiline rows={4}
            value={editContent} onChange={(e) => setEditContent(e.target.value)} sx={{ mb: 2 }} />
          <input accept="image/*" style={{ display: 'none' }} id="edit-image-upload" type="file" onChange={handleEditImageUpload} />
          <label htmlFor="edit-image-upload">
            <Button variant="outlined" component="span" fullWidth sx={{ mb: 2, textTransform: 'none' }}>Change Image</Button>
          </label>
          {editImagePreview && (
            <img src={editImagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveEditPost(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={!editContent.trim()} sx={{ textTransform: 'none' }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Community;
